import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  deleteDoc
} from 'firebase/firestore';
import { Order } from '../types/order';
import { cartService } from './cartService';
import { db } from '../config/firebase';
import { userService } from './userService';

const COLLECTION_NAME = 'orders';

export const orderService = {
  async createOrder(order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>) {
    try {
      // Add order to Firestore
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...order,
        status: 'waiting',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Update cart items status
      const cartItemIds = order.items.map(item => item.id);
      await Promise.all(
        cartItemIds.map(id =>
          cartService.updateCartItem(id, { isOrdered: true })
        )
      );

      return { id: docRef.id, ...order };
    } catch (error) {
      throw new Error('Could not create order: ' + error);
    }
  },

  async getOrdersByUser(userId: string) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
    } catch (error) {
      throw new Error('Could not get orders: ' + error);
    }
  },

  async updateOrderStatus(orderId: string, status: Order['status']) {
    try {
      const docRef = doc(db, COLLECTION_NAME, orderId);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date()
      });

      if (status === 'paid') {
        const order = await this.getOrderById(orderId);
        const points = Math.floor(order.totalAmount / 1000);
        const user = await userService.getUserById(order.userId);

        const hasPoints = user.lstPoint?.some(point => point.orderId === orderId);
        if (!hasPoints && user && order.createdAt) {
          await userService.updateUserPoints(order.userId, orderId, points, order.createdAt);
        }
      }

      return true;
    } catch (error) {
      throw new Error('Could not update order status: ' + error);
    }
  },

  async getAllOrders() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
    } catch (error) {
      throw new Error('Could not get orders: ' + error);
    }
  },

  async getOrderById(orderId: string) {
    try {
      const docRef = doc(db, COLLECTION_NAME, orderId);
      const docSnap = await getDoc(docRef);
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Order;
    } catch (error) {
      throw new Error('Could not get order: ' + error);
    }
  },

  async deleteOrder(orderId: string) {
    try {
      const docRef = doc(db, COLLECTION_NAME, orderId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      throw new Error('Could not delete order: ' + error);
    }
  },

  async removeEquipmentFromOrders(equipmentId: string) {
    try {
      console.log(`Removing equipment ${equipmentId} from all orders`);
      
      // Lấy tất cả đơn hàng
      const q = query(collection(db, COLLECTION_NAME));
      const querySnapshot = await getDocs(q);
      
      let updatedOrdersCount = 0;
      const updatePromises: Promise<any>[] = [];
      
      querySnapshot.forEach((orderDoc) => {
        const orderData = orderDoc.data() as Order;
        
        // Kiểm tra xem đơn hàng có chứa dụng cụ này không
        const hasEquipment = orderData.items.some(item => 
          item.coffeeId === equipmentId || // Có thể dụng cụ được lưu trong coffeeId
          item.drinkId === equipmentId || // hoặc trong các trường khác
          item.dishId === equipmentId ||
          item.id === equipmentId
        );
        
        if (hasEquipment) {
          // Xóa item dụng cụ khỏi đơn hàng
          const updatedItems = orderData.items.filter(item => 
            item.coffeeId !== equipmentId && 
            item.drinkId !== equipmentId && 
            item.dishId !== equipmentId &&
            item.id !== equipmentId
          );
          
          // Tính lại tổng tiền
          const newTotalAmount = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          // Cập nhật đơn hàng
          const updatePromise = updateDoc(doc(db, COLLECTION_NAME, orderDoc.id), {
            items: updatedItems,
            totalAmount: newTotalAmount,
            updatedAt: new Date()
          });
          
          updatePromises.push(updatePromise);
          updatedOrdersCount++;
        }
      });
      
      await Promise.all(updatePromises);
      console.log(`Successfully updated ${updatedOrdersCount} orders by removing equipment ${equipmentId}`);
      
      return updatedOrdersCount;
    } catch (error) {
      console.error('Error removing equipment from orders:', error);
      throw new Error('Could not remove equipment from orders: ' + error);
    }
  }
};